import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { FieldConfiguration } from '@adornis/field-management/field-configuration.js';
import { shouldIncludeFieldHelpers } from '@adornis/field-management/field-management-options.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { ListAspect, ListAspectSchema } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import type { FilterEditorOptions } from '@adornis/filter/filter-options.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { XViewManager } from '@adornis/view-manager/client/x-view-manager.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { m } from '../../localization/out/messages.js';
import { Permission } from '../../permissions/roles.js';
import { UserExport } from '../../users/export/UserExport.js';
import {
  subscribeUserBasedViewProfiles,
  UserBasedViewProfile,
} from '../../view-manager/subscribeUserBasedViewProfiles.js';
import { GroupsAPI } from '../api/GroupsAPI.js';
import { Group } from '../entities/Group.js';
import { Kostenstelle } from '../entities/Kostenstelle.js';
import { School, SchoolSubGroup } from '../entities/School.js';
import { SGGroupCreationDialog } from './sg-group-creation-dialog.js';
import { addPageHeaderAdditional, PageHeader, removePageHeaderAdditional } from '@martinus/styles/client/PageHeader.js';
import { PageTabs } from '@martinus/styles/client/PageTabs.js';

const dataviewWhitelist: BaseQLSelectionSet<Group | School | SchoolSubGroup | Kostenstelle> = {
  name: 1,
  _class: 1,
  bundesland: 1,
  publicSchool: 1,
  schoolType: 1,
  accountingNumber: 1,
  isSgSchool: 1,
  parents: {
    _id: 1,
    name: 1,
    _class: 1,
    diputatsstunden: 1,
    kostenstellePrefix: 1,
    kostenstelleSuffix: 1,
    accountingNumber: 1,
    microsoftDomain: 1,
    abwesenheitsemail: 1,
    schoolType: 1,
  },
  diputatsstunden: 1,
  kostenstellePrefix: 1,
  kostenstelleSuffix: 1,
  microsoftDomain: 1,
  abwesenheitsemail: 1,
  microsoftGroupData: {
    microsoftID: 1,
    mailNickname: 1,
    createdInEntra: 1,
    description: 1,
    secureGroup: 1,
    visibility: 1,
  },
};
const category = 'groups-list';

export const SGGroupsList = stateful(() => {
  const [view, setView] = useState<View<typeof Group> | null>(null);
  const [fallbackView] = useState(new View<typeof Group>({ filter: new AdornisFilter({}) }));
  const user = useObservable(() => currentUser(), []);
  const activeView = view ?? fallbackView;
  if (!activeView.filter) {
    activeView.filter = new AdornisFilter({});
  }
  const activeFilter = activeView.filter;

  const dataViewOptions = {
    class: Group,
    operation: GroupsAPI.subscribeGroups,
    isColumnSelectable: true,
    isColumnResortable: true,
    isColumnResizable: true,
    view: activeView,
    defaultFieldMapping: { number: v => v.toLocaleString('de-DE') },
    fieldSelectionOptions: {
      shouldIncludeField: shouldIncludeFieldHelpers.bySelectionSet({ whitelist: dataviewWhitelist }),
    },
    selectionSet: {
      parentIDs: 1,
    },
    onViewChanged: view => setView(view),
    rowHref: entity =>
      user?.hasPermission(Permission.EDIT_GROUP, entity._id) ||
      entity.parentIDs?.some(id => user?.hasPermission(Permission.EDIT_GROUP, id))
        ? `/groups/${entity._id}`
        : undefined,
    customColumns: [
      {
        fieldName: '_class',
        getFieldMapping: group => m[`_class_${group._class}`](),
      },
    ],
  } satisfies DataViewOptions<typeof Group>;

  const filterOptions = {
    filter: activeFilter,
    searchable: true,
    fieldAspectGeneration: {
      class: Group,
      shouldIncludeField: shouldIncludeFieldHelpers.bySelectionSet({ whitelist: dataviewWhitelist }),
    },
    overridesForFieldName: {
      _class: ListAspectSchema([SchoolSubGroup._class, School._class, Kostenstelle._class], 'Gruppenart'),
    },
    onFilterChanged: filter => {
      activeView.filter = filter;
      setView(activeView);
    },
  } satisfies FilterEditorOptions<typeof Group>;

  const options = buildFullOptions(dataViewOptions, filterOptions);

  const activeTabID = activeFilter.getFilterAspect<ListAspect>('_class')?.values?.[0] ?? 'all';

  // the header slot stores a snapshot of what it renders, so the tabs are registered again whenever the active one changes
  useEffect(() => {
    addPageHeaderAdditional(
      0,
      PageTabs({
        tabs: [
          { key: 'all', label: 'Alle' },
          { key: School._class, label: 'Schulen' },
          { key: Kostenstelle._class, label: 'Kostenstellen' },
        ],
        activeKey: activeTabID,
        onSelect: id => {
          if (id === activeFilter.getFilterAspect<ListAspect>('_class')?.values?.[0]) return;

          if (id === 'all') {
            activeFilter.removeFilterAspect('_class');
          } else {
            activeFilter.setFilterAspect(new ListAspect({ fieldName: '_class', values: [id], mode: 'containsOR' }));
          }
          setView(activeView);
        },
      }),
    );
    return () => removePageHeaderAdditional(0);
  }, [activeTabID]);

  return html`
    ${PageHeader({ title: 'Gruppen' })}

    <div class="card flex flex-col grow m-4 gap-4">
      <div class="flex p-4 justify-between">
        ${FilterEditor({
          noCollapse: true,
          fullWidth: true,
          options: options.filterOptions,
          additionalButtons: () => html`
            <div class="flex gap-4 items-start">
              ${
                user?.hasPermission(Permission.CREATE_GROUP, ANY_CONTEXT)
                  ? html`
                      <button class="btn btn-success" @click=${() => SGGroupCreationDialog()}>
                        <span class="icon-[material-symbols--add-rounded] size-5"></span>
                        Gruppe erstellen
                      </button>
                    `
                  : nothing
              }
              ${XViewManager<typeof UserBasedViewProfile>({
                category,
                operations: {
                  transformViewBeforeCreate: viewProfile => {
                    const newViewProfile = new UserBasedViewProfile(viewProfile);

                    newViewProfile.userId = user?._id;
                    return newViewProfile;
                  },
                  subscribeViews: subscribeUserBasedViewProfiles,
                },
                defaultView: new View({
                  filter: new AdornisFilter({ searchFields: ['name'] }),
                  configuredColumns: [
                    new FieldConfiguration({ fieldName: '_class' }),
                    new FieldConfiguration({ fieldName: 'name' }),
                  ],
                }),
                viewChanged: view => {
                  setView(view as View<typeof Group>);
                  if (view._id)
                    view
                      .save()
                      .then(() => SgSnackbar.show('Ansicht gespeichert'))
                      .catch(error => SgSnackbar.show(error.message));
                },
              })}

              <button
                class="btn btn-primary"
                @click=${() =>
                  exportDataView(options.dataViewOptions, {
                    fileName: `Export_Gruppen_${format(new Date(), 'yyyy-MM-dd-HH-mm')}`,
                    // Just uses better parsing . we should do this parsing by default
                    exportClass: UserExport,
                  })}
              >
                <span class="icon-[material-symbols--download-outline-rounded] size-5"></span>
                Export
              </button>
            </div>
          `,
        })}
      </div>
    </div>

    <div class="overflow-auto bg-white w-full h-full">
      <x-data-view .options=${options.dataViewOptions}></x-data-view>
    </div>
  `;
});
